// Setup Profile scoring + admin Operations Intelligence engine (v8).
// All AI readiness / data maturity / spend signals are INFERRED from operational
// answers. Brands never see this. Cost estimation is deterministic & explainable
// (no external APIs, no scraping) using an internal directional pricing map.

const arr = (v) => (Array.isArray(v) ? v : []);
const has = (v, opt) => arr(v).includes(opt);
const round = (n) => Math.round(n);

const STANDARD_WORKFLOWS = [
  "Inventory counts", "Purchase orders", "Ingredient or packaging costs",
  "Recipes / BOMs", "Production schedules", "Batch or lot records",
  "Finished goods inventory", "Sales orders", "COGS / margins", "Co-packer updates",
];

const WARNING_HELP = [
  "Warn me before I run out of materials",
  "Warn me when inventory is tying up too much cash",
  "Flag margin or cost changes",
];

// ---------- Core scores ----------

export function calculateCompanySizeScore(a) {
  return { "Just me": 0, "2–5": 1, "6–10": 2, "11–25": 3, "26–50": 4,
    "51–100": 5, "101+": 6, "Not sure": 0 }[a.employee_count_range] || 0;
}

export function calculateRevenueScore(a) {
  return { "Pre-revenue": 0, "Under $100K": 1, "$100K–$500K": 2, "$500K–$1M": 3,
    "$1M–$5M": 4, "$5M–$10M": 5, "$10M–$25M": 6, "$25M+": 7,
    "Prefer not to say": 0 }[a.annual_revenue_range] || 0;
}

export function calculateFitScore(a) {
  let s = 0;
  s += { "6–20": 1, "21–50": 2, "51–100": 3, "100+": 4 }[a.sku_range] || 0;
  s += {
    "Daily production": 4, "Multiple production runs per week": 3,
    "Weekly production": 2, "Biweekly production": 2, "Monthly production": 1,
    "Quarterly or seasonal production": 1, "As needed / irregular schedule": 1,
  }[a.production_frequency] || 0;
  const plus3 = ["Spreadsheets", "Paper records / whiteboards",
    "Mostly manual / in someone’s head", "We do not have a clear system"];
  const plus2 = ["Inventory software", "MRP / ERP software",
    "Co-packer reports", "Warehouse / 3PL reports"];
  arr(a.current_operating_methods).forEach((m) => {
    if (plus3.includes(m)) s += 3;
    if (plus2.includes(m)) s += 2;
  });
  return s;
}

export function calculatePainScore(a) {
  const map = {
    "Raw material inventory": 3, "Packaging inventory": 3, "Finished goods inventory": 3,
    "Purchase planning": 3, "Supplier costs": 2, "Recipes / BOMs": 3,
    "Production planning": 3, "Production delays": 3, "Batch records": 3,
    "Lot tracking / traceability": 3, "COGS / margins": 3, "Sales order visibility": 2,
    "Co-packer updates": 3, "Inventory is tying up too much cash": 3,
    "Buying too much, too early, or too late": 3,
  };
  let s = 0;
  arr(a.problem_areas).forEach((p) => { if (map[p]) s += map[p]; });
  return s;
}

export function calculateReplacementOpportunityScore(a) {
  let s = 0;
  ["Spreadsheets", "Paper records / whiteboards",
    "Mostly manual / in someone’s head", "We do not have a clear system"].forEach((m) => {
    if (has(a.current_operating_methods, m)) s += 3;
  });
  if (has(a.operations_data_locations, "Spreadsheet")) s += 3;
  if (has(a.operations_data_locations, "Paper records")) s += 3;
  if (has(a.operations_data_locations, "Someone’s memory")) s += 4;
  if (has(a.operations_data_locations, "Nowhere reliable")) s += 4;
  if (has(a.desired_system_help, "Reduce manual data entry")) s += 3;
  if (has(a.desired_system_help, "Connect my current tools")) s -= 1;
  return s;
}

export function calculateIntegrationUrgencyScore(a) {
  let s = 0;
  const toolMap = {
    "QuickBooks Online": 2, "QuickBooks Desktop": 2, "Xero": 1, "Shopify": 2,
    "NetSuite": 3, "Fishbowl": 2, "Katana": 2, "Cin7": 2,
    "3PL / warehouse portal": 3, "Co-packer portal": 3,
  };
  arr(a.current_tools).forEach((t) => { if (toolMap[t]) s += toolMap[t]; });
  ["QuickBooks", "MRP / ERP system", "Ecommerce platform",
    "Warehouse / 3PL system", "Co-packer reports"].forEach((l) => {
    if (has(a.operations_data_locations, l)) s += 3;
  });
  if (has(a.problem_areas, "Sales order visibility")) s += 2;
  if (has(a.problem_areas, "Co-packer updates")) s += 3;
  if (has(a.desired_system_help, "Connect my current tools")) s += 4;
  return s;
}

export function calculateCentralizationValueScore(a) {
  let s = 0;
  ["Spreadsheet", "Paper records", "Someone’s memory", "Nowhere reliable"].forEach((l) => {
    if (has(a.operations_data_locations, l)) s += 4;
  });
  const dw = arr(a.duplicated_workflows).filter((w) => STANDARD_WORKFLOWS.includes(w));
  if (dw.length >= 5) s += 5;
  else if (dw.length >= 3) s += 3;
  return s;
}

export function calculateDataMaturityScore(a) {
  let s = 0;
  const locMap = {
    "Inventory software": 3, "MRP / ERP system": 4, "QuickBooks": 2,
    "Ecommerce platform": 2, "Warehouse / 3PL system": 2, "Spreadsheet": 1,
    "Paper records": -2, "Someone’s memory": -3, "Nowhere reliable": -4,
  };
  arr(a.operations_data_locations).forEach((l) => { if (locMap[l]) s += locMap[l]; });
  s += { "Check software reports": 3, "Check a spreadsheet": 1,
    "Look across several tools manually": 2, "Estimate based on experience": -2,
    "Wait until there is a problem": -3 }[a.decision_style] || 0;
  return s;
}

export function calculateAutomationReadinessScore(a) {
  let s = 0;
  const helpMap = {
    "Tell me what to buy": 3, "Tell me when to buy": 3,
    "Show what I can produce with current inventory": 3,
    "Warn me before I run out of materials": 3,
    "Warn me when inventory is tying up too much cash": 3,
    "Flag margin or cost changes": 3, "Prioritize what needs attention first": 3,
    "Connect my current tools": 2, "Reduce manual data entry": 2,
  };
  arr(a.desired_system_help).forEach((h) => { if (helpMap[h]) s += helpMap[h]; });
  s += CONTROL_PREF_POINTS[a.recommendation_control_preference] || 0;
  return s;
}

const CONTROL_PREF_POINTS = {
  "Just show me the data; I will decide": 0,
  "Suggest actions, but let me approve everything": 2,
  "Recommend actions and explain why": 3,
  "Automatically flag problems and create tasks": 4,
  "Automatically create purchase or production recommendations for review": 5,
};

export function calculateAiTrustScore(a) {
  let s = 0;
  s += CONTROL_PREF_POINTS[a.recommendation_control_preference] || 0;
  s += { "Check software reports": 2, "Look across several tools manually": 2,
    "Estimate based on experience": -1, "Wait until there is a problem": -2,
  }[a.decision_style] || 0;
  return s;
}

// ---------- Roadmap priority scores ----------

export function calculateRoadmapPriorityScores(a, scores = {}) {
  const r = {
    inventory: 0, purchasing: 0, production_planning: 0, costing: 0,
    traceability: 0, integrations: 0, reporting: 0,
    cash_flow_inventory_planning: 0, ai_recommendations: 0,
    copacker_visibility: 0, tool_consolidation: 0,
  };
  const pa = arr(a.problem_areas);
  const dw = arr(a.duplicated_workflows);
  const dsh = arr(a.desired_system_help);
  const loc = arr(a.operations_data_locations);
  const com = arr(a.current_operating_methods);
  const tools = arr(a.current_tools);

  if (pa.includes("Raw material inventory")) r.inventory += 3;
  if (pa.includes("Packaging inventory")) r.inventory += 3;
  if (pa.includes("Finished goods inventory")) r.inventory += 3;
  if (dw.includes("Inventory counts")) r.inventory += 2;
  if (dsh.includes("Warn me before I run out of materials")) r.inventory += 2;

  if (pa.includes("Purchase planning")) r.purchasing += 3;
  if (pa.includes("Buying too much, too early, or too late")) r.purchasing += 3;
  if (dw.includes("Purchase orders")) r.purchasing += 2;
  if (dsh.includes("Tell me what to buy")) r.purchasing += 3;
  if (dsh.includes("Tell me when to buy")) r.purchasing += 3;

  if (pa.includes("Production planning")) r.production_planning += 3;
  if (pa.includes("Production delays")) r.production_planning += 3;
  if (dw.includes("Production schedules")) r.production_planning += 2;
  if (dsh.includes("Show what I can produce with current inventory")) r.production_planning += 3;

  if (pa.includes("COGS / margins")) r.costing += 3;
  if (pa.includes("Supplier costs")) r.costing += 2;
  if (dw.includes("COGS / margins")) r.costing += 2;
  if (dsh.includes("Help calculate true product costs")) r.costing += 3;
  if (dsh.includes("Flag margin or cost changes")) r.costing += 3;

  if (pa.includes("Batch records")) r.traceability += 3;
  if (pa.includes("Lot tracking / traceability")) r.traceability += 3;
  if (dw.includes("Batch or lot records")) r.traceability += 2;
  if (dsh.includes("Track lots, batches, and traceability")) r.traceability += 3;

  if (dsh.includes("Connect my current tools")) r.integrations += 4;
  if (loc.includes("Ecommerce platform")) r.integrations += 2;
  if (loc.includes("Warehouse / 3PL system")) r.integrations += 2;
  if (loc.includes("QuickBooks")) r.integrations += 2;
  if (tools.includes("QuickBooks Online")) r.integrations += 2;
  if (tools.includes("Shopify")) r.integrations += 2;
  if (tools.includes("NetSuite")) r.integrations += 3;

  if (dsh.includes("Improve reporting and visibility")) r.reporting += 3;
  if (dsh.includes("Prioritize what needs attention first")) r.reporting += 3;
  if (a.decision_style === "Check software reports") r.reporting += 2;
  if (a.decision_style === "Look across several tools manually") r.reporting += 2;

  if (pa.includes("Inventory is tying up too much cash")) r.cash_flow_inventory_planning += 4;
  if (pa.includes("Buying too much, too early, or too late")) r.cash_flow_inventory_planning += 3;
  if (dsh.includes("Warn me when inventory is tying up too much cash")) r.cash_flow_inventory_planning += 4;

  if (dsh.includes("Tell me what to buy")) r.ai_recommendations += 2;
  if (dsh.includes("Tell me when to buy")) r.ai_recommendations += 2;
  if (dsh.includes("Show what I can produce with current inventory")) r.ai_recommendations += 2;
  if (dsh.includes("Warn me before I run out of materials")) r.ai_recommendations += 2;
  if (dsh.includes("Flag margin or cost changes")) r.ai_recommendations += 2;
  if (dsh.includes("Prioritize what needs attention first")) r.ai_recommendations += 2;
  if (a.recommendation_control_preference &&
      a.recommendation_control_preference !== "Just show me the data; I will decide") {
    r.ai_recommendations += 3;
  }

  if (com.includes("Co-packer reports")) r.copacker_visibility += 3;
  if (loc.includes("Co-packer reports")) r.copacker_visibility += 3;
  if (pa.includes("Co-packer updates")) r.copacker_visibility += 3;
  if (dw.includes("Co-packer updates")) r.copacker_visibility += 2;

  if ((scores.replacement_opportunity_score || 0) >= 6) r.tool_consolidation += 3;
  if ((scores.integration_urgency_score || 0) >= 8) r.tool_consolidation += 2;
  ["Fishbowl", "Katana", "Cin7", "MRPeasy"].forEach((t) => {
    if (tools.includes(t)) r.tool_consolidation += 3;
  });
  ["Airtable", "Notion", "Excel / Google Sheets"].forEach((t) => {
    if (tools.includes(t)) r.tool_consolidation += 2;
  });
  return r;
}

// ---------- Estimated systems cost engine (ADMIN ONLY) ----------

export const PRICING_MAP = {
  "QuickBooks Online": { low: 30, high: 200, category: "accounting", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "medium" },
  "QuickBooks Desktop": { low: 60, high: 300, category: "accounting", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "medium" },
  "Xero": { low: 20, high: 100, category: "accounting", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "medium" },
  "Shopify": { low: 40, high: 400, category: "ecommerce", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "medium" },
  "WooCommerce": { low: 20, high: 250, category: "ecommerce", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "Amazon": { low: 40, high: 300, category: "ecommerce", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "Faire": { low: 0, high: 200, category: "ecommerce", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "Fishbowl": { low: 300, high: 1200, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Katana": { low: 200, high: 1000, category: "mrp_erp", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Cin7": { low: 350, high: 1500, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "MRPeasy": { low: 50, high: 600, category: "mrp_erp", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Odoo": { low: 50, high: 1000, category: "mrp_erp", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "low" },
  "NetSuite": { low: 1000, high: 8000, category: "mrp_erp", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "medium" },
  "Sage": { low: 300, high: 3000, category: "mrp_erp", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "low" },
  "Microsoft Dynamics": { low: 500, high: 5000, category: "mrp_erp", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "low" },
  "Acumatica": { low: 800, high: 5000, category: "mrp_erp", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "low" },
  "SOS Inventory": { low: 60, high: 400, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Finale Inventory": { low: 100, high: 800, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Unleashed": { low: 300, high: 1200, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "medium" },
  "Wherefour": { low: 300, high: 2000, category: "mrp_erp", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "medium", confidence: "low" },
  "Craftybase": { low: 30, high: 200, category: "inventory", replace_or_integrate: "possible_replacement", replacement_likelihood: "medium", integration_likelihood: "low", confidence: "medium" },
  "ShipStation": { low: 10, high: 250, category: "shipping_fulfillment", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "medium" },
  "3PL / warehouse portal": { low: 100, high: 1500, category: "warehouse_3pl", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "low" },
  "EDI / retailer portals": { low: 100, high: 2000, category: "edi_retailer", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "high", confidence: "low" },
  "SafetyChain": { low: 500, high: 5000, category: "quality_compliance", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "TraceGains": { low: 500, high: 5000, category: "quality_compliance", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "FoodLogiQ": { low: 500, high: 5000, category: "quality_compliance", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
  "Excel / Google Sheets": { low: 0, high: 50, category: "spreadsheet_manual", replace_or_integrate: "replace", replacement_likelihood: "high", integration_likelihood: "low", confidence: "medium" },
  "Airtable": { low: 20, high: 300, category: "workflow_project_management", replace_or_integrate: "replace", replacement_likelihood: "high", integration_likelihood: "low", confidence: "medium" },
  "Notion": { low: 0, high: 200, category: "workflow_project_management", replace_or_integrate: "replace", replacement_likelihood: "high", integration_likelihood: "low", confidence: "medium" },
  "Paper records": { low: 0, high: 0, category: "spreadsheet_manual", replace_or_integrate: "replace", replacement_likelihood: "high", integration_likelihood: "low", confidence: "high" },
  "Co-packer portal": { low: 0, high: 500, category: "copacker_portal", replace_or_integrate: "integrate", replacement_likelihood: "low", integration_likelihood: "medium", confidence: "low" },
};

const ENTERPRISE_HEAVY = new Set([
  "NetSuite", "Sage", "Microsoft Dynamics", "Acumatica",
  "SafetyChain", "TraceGains", "FoodLogiQ", "EDI / retailer portals", "3PL / warehouse portal",
]);

const REVENUE_MULTIPLIER = {
  "Pre-revenue": 0.5, "Under $100K": 0.75, "$100K–$500K": 0.9, "$500K–$1M": 1.0,
  "$1M–$5M": 1.1, "$5M–$10M": 1.25, "$10M–$25M": 1.4, "$25M+": 1.6,
  "Prefer not to say": 1.0,
};
const EMPLOYEE_MULTIPLIER = {
  "Just me": 0.75, "2–5": 0.9, "6–10": 1.0, "11–25": 1.1, "26–50": 1.25,
  "51–100": 1.4, "101+": 1.6, "Not sure": 1.0,
};

function costBand(monthlyMid) {
  if (monthlyMid === 0) return "no_detected_spend";
  if (monthlyMid < 100) return "under_100";
  if (monthlyMid <= 300) return "100_to_300";
  if (monthlyMid <= 750) return "301_to_750";
  if (monthlyMid <= 1500) return "751_to_1500";
  if (monthlyMid <= 3000) return "1501_to_3000";
  return "3000_plus";
}

export function estimateSystemsCost(a) {
  const tools = arr(a.current_tools).filter((t) => t && t !== "None");
  const revMult = REVENUE_MULTIPLIER[a.annual_revenue_range] ?? 1.0;
  const empMult = EMPLOYEE_MULTIPLIER[a.employee_count_range] ?? 1.0;

  let totalLow = 0, totalHigh = 0;
  let replLow = 0, replHigh = 0, intLow = 0, intHigh = 0;
  let unknownCount = 0, enterpriseCount = 0;
  const breakdown = [];
  const notes = [];

  tools.forEach((tool) => {
    const base = PRICING_MAP[tool];
    let mLow, mHigh, category, roi, rl, il, conf, note = "";
    if (base) {
      mLow = round(base.low * revMult * empMult);
      mHigh = round(base.high * revMult * empMult);
      category = base.category; roi = base.replace_or_integrate;
      rl = base.replacement_likelihood; il = base.integration_likelihood; conf = base.confidence;
      if (ENTERPRISE_HEAVY.has(tool)) enterpriseCount += 1;
    } else {
      mLow = 0; mHigh = round(300 * revMult * empMult);
      category = "unknown_custom"; roi = "unknown";
      rl = "unknown"; il = "unknown"; conf = "low";
      note = "Custom tool added by user; estimate is low confidence.";
      unknownCount += 1;
      notes.push(`Unknown custom tool "${tool}" — estimated at low confidence.`);
    }

    totalLow += mLow; totalHigh += mHigh;
    if (roi === "replace") { replLow += mLow; replHigh += mHigh; }
    else if (roi === "possible_replacement") {
      replLow += round(mLow * 0.5); replHigh += round(mHigh * 0.5);
      intLow += round(mLow * 0.5); intHigh += round(mHigh * 0.5);
    } else if (roi === "integrate") { intLow += mLow; intHigh += mHigh; }

    breakdown.push({
      tool, category,
      estimated_monthly_low: mLow, estimated_monthly_high: mHigh,
      estimated_annual_low: mLow * 12, estimated_annual_high: mHigh * 12,
      replace_or_integrate: roi, replacement_likelihood: rl,
      integration_likelihood: il, confidence: conf, notes: note,
    });
  });

  const monthlyMid = round((totalLow + totalHigh) / 2);
  const band = costBand(monthlyMid);

  // Confidence
  const wide = (totalHigh - totalLow) > 4000;
  let confidence = "high";
  if (unknownCount >= 2 || enterpriseCount >= 2 || (enterpriseCount >= 1 && tools.length >= 3) || wide) {
    confidence = "low";
  } else if (unknownCount >= 1 || enterpriseCount >= 1) {
    confidence = "medium";
  }

  // Pricing interpretation
  let interpretation;
  if (monthlyMid <= 300) {
    interpretation = "This account appears to have limited current systems spend. Pricing should emphasize time savings, setup simplicity, and replacing manual spreadsheet work rather than software consolidation.";
  } else if (monthlyMid <= 1500) {
    interpretation = "This account appears to have meaningful current systems spend. Position the product around consolidating operational workflows, reducing manual entry, and connecting key systems rather than replacing accounting or ecommerce tools.";
  } else {
    interpretation = "This account appears to have significant systems spend and likely implementation complexity. Prioritize integration needs, workflow consolidation, and potential onboarding support.";
  }
  if (replHigh > 1000) {
    interpretation += " There may be meaningful replaceable spend tied to inventory, MRP, workflow, or spreadsheet-based tools. This account may be a candidate for migration-focused positioning.";
  }
  if (intHigh > replHigh) {
    interpretation += " Much of this account’s systems spend appears tied to tools that should likely be integrated rather than replaced.";
  }

  if (tools.length === 0) {
    notes.push("No paid tools detected; brand may rely on manual/spreadsheet workflows.");
  }

  return {
    estimated_monthly_systems_cost_low: totalLow,
    estimated_monthly_systems_cost_high: totalHigh,
    estimated_monthly_systems_cost_midpoint: monthlyMid,
    estimated_annual_systems_cost_low: totalLow * 12,
    estimated_annual_systems_cost_high: totalHigh * 12,
    estimated_annual_systems_cost_midpoint: monthlyMid * 12,
    estimated_systems_cost_band: band,
    estimated_systems_cost_confidence: confidence,
    estimated_replaceable_monthly_cost_low: replLow,
    estimated_replaceable_monthly_cost_high: replHigh,
    estimated_integrate_not_replace_monthly_cost_low: intLow,
    estimated_integrate_not_replace_monthly_cost_high: intHigh,
    system_cost_breakdown: breakdown,
    system_cost_estimation_notes: notes,
    pricing_interpretation: interpretation,
  };
}

// ---------- Account value ----------

export function calculateAccountValueScore(scores, cost) {
  let s = (scores.company_size_score || 0) + (scores.revenue_score || 0) +
    (scores.fit_score || 0) + (scores.pain_score || 0);
  const band = cost.estimated_systems_cost_band;
  if (band === "301_to_750") s += 2;
  else if (band === "751_to_1500") s += 3;
  else if (band === "1501_to_3000") s += 4;
  else if (band === "3000_plus") s += 5;
  const replHigh = cost.estimated_replaceable_monthly_cost_high || 0;
  if (replHigh >= 1000) s += 3;
  else if (replHigh >= 500) s += 2;
  return s;
}

// ---------- Segments ----------

const ROADMAP_ORDER = [
  "purchasing", "cash_flow_inventory_planning", "costing", "production_planning",
  "traceability", "copacker_visibility", "integrations", "tool_consolidation",
  "inventory", "reporting", "ai_recommendations",
];
const ROADMAP_TO_SEGMENT = {
  purchasing: "purchasing_stockout_buyer",
  cash_flow_inventory_planning: "cash_flow_inventory_planning_buyer",
  costing: "costing_margin_buyer",
  production_planning: "production_planning_buyer",
  traceability: "traceability_compliance_buyer",
  copacker_visibility: "copacker_visibility_buyer",
  integrations: "integration_heavy_buyer",
  tool_consolidation: "spreadsheet_replacement_buyer",
  inventory: "purchasing_stockout_buyer",
  reporting: "general_operations_buyer",
  ai_recommendations: "general_operations_buyer",
};

export function determinePrimarySegment(a, scores, roadmap) {
  if (a.sku_range === "1–5" && a.annual_revenue_range === "Pre-revenue" &&
      a.production_frequency === "Pre-production / not producing yet") {
    return "weak_low_urgency_user";
  }
  let maxKey = null, maxVal = -Infinity;
  ROADMAP_ORDER.forEach((k) => {
    const v = roadmap[k] || 0;
    if (v > maxVal) { maxVal = v; maxKey = k; }
  });
  if (maxVal <= 0) {
    if ((scores.replacement_opportunity_score || 0) >= 6) return "spreadsheet_replacement_buyer";
    return "general_operations_buyer";
  }
  return ROADMAP_TO_SEGMENT[maxKey] || "general_operations_buyer";
}

export function determineSecondarySegment(a, scores, cost) {
  if (scores.data_maturity_score <= 0) return "data_cleanup_needed";
  if (scores.ai_trust_score >= 4 && scores.automation_readiness_score >= 6) return "ai_recommendation_ready";
  if (a.recommendation_control_preference === "Recommend actions and explain why") return "ai_cautious_needs_explainability";
  if (a.recommendation_control_preference === "Just show me the data; I will decide") return "tracking_only_user";
  if (scores.integration_urgency_score >= 8) return "integration_heavy_buyer";
  if (scores.replacement_opportunity_score >= 6) return "manual_workflow_replacement";
  const band = cost.estimated_systems_cost_band;
  if (band === "1501_to_3000" || band === "3000_plus") return "high_estimated_systems_spend";
  if (cost.estimated_replaceable_monthly_cost_high >= 1000) return "high_replaceable_spend";
  if (cost.estimated_integrate_not_replace_monthly_cost_high > cost.estimated_replaceable_monthly_cost_high) {
    return "integration_bound_spend";
  }
  return "tracking_only_user";
}

export function determineRecommendedAiExperience(a, scores) {
  const rcp = a.recommendation_control_preference;
  if (scores.data_maturity_score <= 0) return "data_cleanup_first";
  if (rcp === "Just show me the data; I will decide" && scores.automation_readiness_score <= 2) return "tracking_only";
  if (arr(a.desired_system_help).some((h) => WARNING_HELP.includes(h)) && scores.ai_trust_score <= 2) return "basic_alerts";
  if (rcp === "Recommend actions and explain why") return "explainable_recommendations";
  if (rcp === "Suggest actions, but let me approve everything" ||
      rcp === "Automatically create purchase or production recommendations for review") return "review_required_automation";
  if (rcp === "Automatically flag problems and create tasks") return "proactive_task_generation";
  return "basic_alerts";
}

export function determineRecommendedOnboardingPath(primarySegment) {
  return {
    purchasing_stockout_buyer: "purchasing_setup_path",
    cash_flow_inventory_planning_buyer: "inventory_cash_planning_path",
    costing_margin_buyer: "costing_setup_path",
    production_planning_buyer: "production_planning_path",
    traceability_compliance_buyer: "traceability_setup_path",
    copacker_visibility_buyer: "copacker_visibility_path",
    spreadsheet_replacement_buyer: "spreadsheet_import_path",
    integration_heavy_buyer: "integration_setup_path",
    weak_low_urgency_user: "basic_template_path",
  }[primarySegment] || "general_setup_path";
}

export function determineRecommendedSalesAction(scores, cost, primarySegment) {
  if (scores.fit_score >= 9 && scores.pain_score >= 8) return "offer_onboarding_call";
  if (scores.account_value_score >= 22 && scores.pain_score >= 6) return "offer_onboarding_call";
  if (cost.estimated_replaceable_monthly_cost_high >= 1000) return "offer_onboarding_call";
  if (scores.integration_urgency_score >= 8) return "integration_followup";
  if (primarySegment === "weak_low_urgency_user") return "low_touch_nurture";
  return "self_serve_guided_setup";
}

// ---------- Orchestrator ----------

export function calculateSetupProfileResults(answers) {
  const scores = {
    fit_score: calculateFitScore(answers),
    pain_score: calculatePainScore(answers),
    company_size_score: calculateCompanySizeScore(answers),
    revenue_score: calculateRevenueScore(answers),
    replacement_opportunity_score: calculateReplacementOpportunityScore(answers),
    integration_urgency_score: calculateIntegrationUrgencyScore(answers),
    centralization_value_score: calculateCentralizationValueScore(answers),
    data_maturity_score: calculateDataMaturityScore(answers),
    automation_readiness_score: calculateAutomationReadinessScore(answers),
    ai_trust_score: calculateAiTrustScore(answers),
  };
  const roadmap_priority_scores = calculateRoadmapPriorityScores(answers, scores);
  const cost = estimateSystemsCost(answers);
  scores.account_value_score = calculateAccountValueScore(scores, cost);

  const primary_segment = determinePrimarySegment(answers, scores, roadmap_priority_scores);
  const secondary_segment = determineSecondarySegment(answers, scores, cost);
  const recommended_ai_experience = determineRecommendedAiExperience(answers, scores);
  const recommended_onboarding_path = determineRecommendedOnboardingPath(primary_segment);
  const recommended_sales_action = determineRecommendedSalesAction(scores, cost, primary_segment);

  return {
    ...scores,
    roadmap_priority_scores,
    primary_segment,
    secondary_segment,
    recommended_onboarding_path,
    recommended_sales_action,
    recommended_ai_experience,
    ...cost,
  };
}

// ---------- Display helpers ----------

export function recommendationSentence(segment) {
  const map = {
    costing_margin_buyer: "Priority: accurate COGS and margins. Start by structuring recipes, ingredient costs, and overhead.",
    purchasing_stockout_buyer: "Priority: preventing stockouts and smarter purchasing. Start by centralizing purchasing, lead times, and reorder points.",
    cash_flow_inventory_planning_buyer: "Priority: freeing up cash trapped in inventory. Start by aligning purchasing cadence with real demand.",
    production_planning_buyer: "Priority: a calmer production plan. Start by centralizing schedules, capacity, and batch sequencing.",
    traceability_compliance_buyer: "Priority: lot-level traceability. Start by capturing batch and compliance records in one place.",
    copacker_visibility_buyer: "Priority: visibility into co-packer activity. Start by centralizing co-packer updates and production status.",
    integration_heavy_buyer: "Priority: connecting existing systems. Start with QuickBooks, Shopify, and 3PL integrations.",
    spreadsheet_replacement_buyer: "Priority: replacing the spreadsheet/tool patchwork. Start by importing existing sheets into one structured system.",
    weak_low_urgency_user: "Early stage. Start with a lightweight template and revisit when production picks up.",
    general_operations_buyer: "Priority: centralizing day-to-day operations. Start with inventory, purchasing, and production in one place.",
  };
  return map[segment] || map.general_operations_buyer;
}

export function humanizeKey(key) {
  return String(key || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
